import {Template} from 'Event/template'
import {flatten} from 'lib/object'
import {DeepPartialSubstitute} from 'lib/type-utils'
import React, {useState, useCallback, useEffect, useMemo} from 'react'
import setAtPath from 'lodash/set'
import unsetAtPath from 'lodash/unset'
import getAtPath from 'lodash/get'
import {useOrganization} from 'organization/OrganizationProvider'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import {ObvioEvent} from 'Event'
import {useEventSocket} from 'organization/Event/EventSocketProvider'
import moment from 'moment'
import {useDispatch} from 'react-redux'
import {setIsConnected} from 'Event/Dashboard/editor/state/actions'
import {clone} from 'ramda'

// Updated template event broadcasted by Laravel via Ably
const UPDATED_TEMPLATE = '.template.updated'

// Updated data sent via real-time socket to notify other users of any updates
type TemplateUpdate = {
  updated_at: string
  template: KeyPaths
}

// When we want the server to remove a key, we'll set it to a
// specific remove value.
export const REMOVE = '__REMOVE__'

export type TemplateSave<T> = (
  updates: DeepPartialSubstitute<T, typeof REMOVE>,
) => void

const TemplateUpdateContext = React.createContext<
  | {
      update: TemplateSave<Template>

      /**
       * Stage the original template used to calculate the delta/diff
       * so that only changed keys are sent.
       */
      stageOriginal: () => void
    }
  | undefined
>(undefined)

// A dictionary of the updated template keys, and their
// respective values.
type KeyPaths = Record<string, any>

export default function TemplateUpdateProvider(props: {
  children: (template: Template) => React.ReactElement
  template: Template
}) {
  const {template: saved} = props

  // Normalize waiver.targets to ensure it's always an object, not an array
  // This prevents react-hook-form from creating form state for array indices
  const normalizedTemplate = {...saved}
  if (
    normalizedTemplate.waiver?.targets &&
    Array.isArray(normalizedTemplate.waiver.targets)
  ) {
    normalizedTemplate.waiver.targets = {}
  }

  const [current, setCurrent] = useState(normalizedTemplate)
  // Delta is calculated against original because current could
  // include new updates coming in from socket.
  const [original, setOriginal] = useState<Template>(current)
  const save = useSave()

  // If the template has been changed entirely, such as moving
  // from SimpleBlog -> Cards, then we'll replace the
  // current template.
  useEffect(() => {
    if (saved.name === current.name) {
      return
    }

    // Normalize waiver.targets when template changes
    const normalized = {...saved}
    if (
      normalized.waiver?.targets &&
      Array.isArray(normalized.waiver.targets)
    ) {
      normalized.waiver.targets = {}
    }
    setCurrent(normalized)
  }, [saved, current])

  const {channel} = useEventSocket()
  const dispatch = useDispatch()

  // Updates are a dictionary of all successful property updates,
  // and their respective timestamps.
  const updates: Record<string, string> = useMemo(() => ({}), [])

  // Filter update data to only return keys that are NEWER than any updates
  // we've alredy saved. This prevents any flickering we may see if our
  // save went through, but an older update comes through after.
  const onlyNewUpdates = useCallback(
    (data: TemplateUpdate) =>
      Object.entries(data.template).reduce((acc, [key, val]) => {
        const lastSaved = updates[key]
        if (!lastSaved) {
          acc[key] = val
          return acc
        }

        const lastSavedTime = moment(lastSaved)
        const updateTime = moment(data.updated_at)
        const isNewer = updateTime.isAfter(lastSavedTime)

        // We already have a save that should be newer than this, so
        // let's ignore it.
        if (!isNewer) {
          return acc
        }

        acc[key] = val
        return acc
      }, {} as KeyPaths),
    [updates],
  )

  // Since the last write wins, we'll record the timestamp on any successful save.
  // This way if any updates via socket come in for previous saves, we can
  // safely ignore, knowing our saved value will eventually arrive.
  const recordUpdates = useCallback(
    (lastSaved: string, keyPaths: KeyPaths) => {
      for (const key of Object.keys(keyPaths)) {
        updates[key] = lastSaved
      }
    },
    [updates],
  )

  // Set the values in the template for the given keypaths.
  const set = useCallback(
    (keyPaths: KeyPaths) => {
      setCurrent(applyKeyPaths(current, keyPaths))
    },
    [current],
  )

  // Update the template, this is where the type-safety checks happen, so this
  // should be the only exposed method to other parts of the app.
  const update: TemplateSave<typeof current> = useCallback(
    (updates) => {
      const keyPaths = flatten(updates)

      // Delta sync: only send keys that actually changed to prevent
      // concurrent edits to different properties from overwriting each other.
      const changed = onlyChangedKeys(keyPaths, original || {})
      if (Object.keys(changed).length === 0) {
        return
      }

      set(changed)
      setOriginal((original) => applyKeyPaths(original, changed))
      save(changed)
        .then(({updated_at}) => recordUpdates(updated_at, changed))
        .catch(() => {
          // Save failed for some reason, let's force a user refresh
          dispatch(setIsConnected(false))
        })
    },
    [dispatch, recordUpdates, set, save, original],
  )

  // Handle live updates from server
  useEffect(() => {
    channel?.listen(UPDATED_TEMPLATE, (data: TemplateUpdate) => {
      const newUpdates = onlyNewUpdates(data)
      recordUpdates(data.updated_at, newUpdates)
      set(newUpdates)
    })

    return () => {
      channel?.stopListening(UPDATED_TEMPLATE)
    }
  }, [channel, onlyNewUpdates, recordUpdates, set])

  const stageOriginal = useCallback(() => {
    setOriginal(current)
  }, [setOriginal, current])

  return (
    <TemplateUpdateContext.Provider
      value={{
        update,
        stageOriginal,
      }}
    >
      {props.children(current)}
    </TemplateUpdateContext.Provider>
  )
}

function applyKeyPaths(template: Template, keyPaths: KeyPaths) {
  // We're using lodash's _.set() func here which unfortunately is a
  // mutating function, so we'll need to clone the template before
  // modifying it to avoid potential shared state bugs. Note
  // we're using Ramda's clone, because Lodash's had a
  // weird bug that wouldn't clone an empty object.
  const updated = clone(template)

  for (const [keyPath, val] of Object.entries(keyPaths)) {
    if (val === REMOVE) {
      unsetAtPath(updated, keyPath)
      continue
    }

    if (val === undefined) {
      continue
    }
    setAtPathWithObjectCasting(updated, keyPath, val)
  }

  // Normalize waiver.targets after updates to ensure it's always an object
  if (updated.waiver?.targets && Array.isArray(updated.waiver.targets)) {
    updated.waiver.targets = {}
  }

  return updated
}

/**
 * Sets a value on an object at the given key path. This is an extension that
 * will automatically convert an [] into a {} if you are trying to set a
 * key that is a string.
 *
 * Fixes a bug where the server may cast objects, such as `speakers.items` from {} -> [].
 * When we call `setAtPath` on an array with a string it attempts to use it as an
 * array index which results in weird downstream.
 */
export function setAtPathWithObjectCasting<T extends Record<string, any>>(
  object: T,
  path: string,
  value: any,
): T {
  // keypaths are in the format `speakers.items.{uuid}.name`
  const parts = path.split('.')
  const key = parts[parts.length - 2] // {uuid}

  // If the last key is a number, we'll assume no casting is required as
  // we're only worried about setting a string on an [].
  const isNumberKey = Number.isInteger(Number(key))
  if (isNumberKey) {
    setAtPath(object, path, value)
    return object
  }

  const isList = parts.length > 2 // at least 3 parts
  if (!isList) {
    setAtPath(object, path, value)
    return object
  }

  // Finally, we want to check if we're setting a string on an Array[] here,
  // and if we are, let's convert it into an object{} before we do.
  const listKey = parts.filter((_p, i) => i < parts.length - 2).join('.')
  const listVal = getAtPath(object, listKey)
  if (Array.isArray(listVal) && listVal.length === 0) {
    setAtPath(object, listKey, {})
  }

  setAtPath(object, path, value)
  return object
}

/**
 * Filters keyPaths to only include keys whose values differ from the current
 * template state. This enables delta-sync by only sending changed properties,
 * preventing concurrent edits to different properties from overwriting each other.
 */
export function onlyChangedKeys(
  keyPaths: KeyPaths,
  current: Record<string, any>,
): KeyPaths {
  const changed: KeyPaths = {}

  for (const [key, val] of Object.entries(keyPaths)) {
    const currentVal = getAtPath(current, key)

    // Always include REMOVE operations
    if (val === REMOVE) {
      changed[key] = val
      continue
    }

    // Treat empty strings the same as nulls as they get casted to null
    if (val === '' && currentVal === null) {
      continue
    }

    // Include if value is different
    if (currentVal !== val) {
      changed[key] = val
    }
  }

  return changed
}

function useSave() {
  const {client} = useOrganization()
  const {event} = useEvent()

  const url = api(`/events/${event.id}/template`)

  return useCallback(
    (keyPaths: Record<string, any>) =>
      client.put<ObvioEvent>(url, {template: keyPaths}),
    [client, url],
  )
}

export function useSaveTemplate<T = Template>() {
  const context = React.useContext(TemplateUpdateContext)
  if (context === undefined) {
    throw new Error(
      'useSaveTemplate must be used within a TemplateUpdateProvider',
    )
  }

  return (context.update as unknown) as TemplateSave<T>
}

interface UseAutoStageTemplateParams {
  when: boolean
}

/**
 * On config open stage the template so that
 * the diff/delta only syncs with actual
 * user changes without affecting socket updates
 */
export function useAutoStageTemplate(params: UseAutoStageTemplateParams) {
  const {when: shouldStage} = params

  const context = React.useContext(TemplateUpdateContext)
  if (context === undefined) {
    throw new Error(
      'useAutoStageTemplate must be used within a TemplateUpdateProvider',
    )
  }

  useEffect(() => {
    if (shouldStage) {
      context.stageOriginal()
    }

    // Rare case where we disable exhaustive deps to prevent downstream
    // state race conditions because child components are likely
    // calling stage in a useEffect with isVisible/showing.

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStage])
}
